import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, RotateCcw, Trash2, ArrowLeft, Camera } from 'lucide-react';
import Captcha2 from '../UtilityComponents/Captcha2';
import UserSettingsReq from '../UtilityComponents/UserSettingsReq';
import { uploadAvatar, deleteAvatar, loadAvatar } from '../UtilityComponents/AvatarService';
import SettingsPageStyle from '../styles&pics/SettingsPageStyle';

const SettingsPage = ({ user, handleLogout }) => {
  const [activeOption, setActiveOption] = useState(null);
  const [newusername, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetexams, setResetexams] = useState(false);
  const [deleteac, setDeleteac] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hp, setHP] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const navigate = useNavigate();

  // Load avatar on mount
  useEffect(() => {
    const loadUserAvatar = async () => {
      const avatar = await loadAvatar(user);
      if (avatar) {
        setAvatarPreview(avatar);
      }
    };
    loadUserAvatar();
  }, [user]);

  const isValidEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 500KB)
      if (file.size > 500000) {
        alert('Το αρχείο είναι πολύ μεγάλο. Μέγιστο μέγεθος: 500KB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Παρακαλώ επιλέξτε μια εικόνα');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        setAvatarPreview(base64String);
        
        try {
          // Upload to server
          await uploadAvatar(base64String);
          
          // Also save to localStorage for immediate access
          localStorage.setItem(`avatar_${user}`, base64String);
          window.dispatchEvent(new Event('avatarChange'));
          
          alert('Το avatar ενημερώθηκε επιτυχώς!');
        } catch (error) {
          alert('Σφάλμα κατά την αποθήκευση του avatar: ' + error.message);
          setAvatarPreview(localStorage.getItem(`avatar_${user}`) || '');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      // Delete from server
      await deleteAvatar();
      
      // Remove from localStorage
      localStorage.removeItem(`avatar_${user}`);
      setAvatarPreview('');
      window.dispatchEvent(new Event('avatarChange'));
      
      alert('Το avatar αφαιρέθηκε επιτυχώς!');
    } catch (error) {
      alert('Σφάλμα κατά την αφαίρεση του avatar: ' + error.message);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    if (hp !== '') {
      return;
    }

    if (newusername === user) {
      alert("Παρακαλώ εισάγετε διαφορετικό όνομα χρήστη από το τρέχων.");
      setIsLoading(false);
      return;
    }

    if (newPassword === password) {
      alert("Ο κωδικός είναι ίδιος με τον τρέχων κωδικό σας.");
      setIsLoading(false);
      return;
    }

    if (newPassword !== '' && newPassword.length < 8) {
      alert("Ελάχιστο μήκος κωδικού 8 χαρακτήρες.");
      setIsLoading(false);
      return;
    }

    if ((newPassword !== '' && confirmPassword !== '') && newPassword !== confirmPassword) {
      alert("Οι κωδικοί δεν ταιριάζουν!");
      setIsLoading(false);
      return;
    }

    if (email !== '' && !isValidEmail(email)) {
      alert("Παρακαλώ εισάγετε μια έγκυρη διεύθυνση email.");
      setIsLoading(false);
      return;
    }

    const res = await UserSettingsReq({
      username: user,
      newusername: newusername || null,
      password: password,
      email: email || null,
      newPassword: newPassword || null,
      resetexams: resetexams,
      deleteac: deleteac
    });

    setIsLoading(false);

    if (res.success === true) {
      if (newusername || newPassword) {
        alert("Επιτυχής αλλαγή νέων credentials. Παρακαλώ συνδεθείτε ξανά με τα νέα σας στοιχεία.");
        handleLogout();
        navigate('/');
      }
      if (resetexams === true) {
        alert("Έγινε reset των στατιστικών σας");
        setActiveOption(null);
        setResetexams(false);
      }
      if (email) {
        alert("Επιτυχής αλλαγή email.");
        setActiveOption(null);
      }
      if (deleteac === true) {
        alert("Επιτυχής διαγραφή λογαριασμού. Ευχόμαστε να επιτύχατε στην εξέταση!");
        handleLogout();
        navigate('/');
      }
    } else {
      alert(res.message);
    }
  };

  const settingsOptions = [
    {
      id: 'avatar',
      icon: <Camera />,
      title: 'Αλλαγή Avatar',
      description: 'Ανεβάστε μια φωτογραφία προφίλ',
      color: '#ec4899',
      noForm: true
    },
    {
      id: 'usernamechange',
      icon: <User />,
      title: 'Αλλαγή Username',
      description: 'Αλλάξτε το όνομα χρήστη σας',
      color: '#3b82f6'
    },
    {
      id: 'emailchange',
      icon: <Mail />,
      title: 'Αλλαγή Email',
      description: 'Ενημερώστε τη διεύθυνση email σας',
      color: '#8b5cf6'
    },
    {
      id: 'passwordchange',
      icon: <Lock />,
      title: 'Αλλαγή Password',
      description: 'Αλλάξτε τον κωδικό πρόσβασής σας',
      color: '#10b981'
    },
    {
      id: 'resetexams',
      icon: <RotateCcw />,
      title: 'Reset Στατιστικών',
      description: 'Διαγραφή όλων των στατιστικών σας',
      color: '#f59e0b'
    },
    {
      id: 'deleteaccount',
      icon: <Trash2 />,
      title: 'Διαγραφή Account',
      description: 'Μόνιμη διαγραφή του λογαριασμού σας',
      color: '#ef4444'
    }
  ];

  return (
    <>
      <SettingsPageStyle />
      <div className="settings-page">
        <div className="settings-container">
          <div className="settings-header">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <ArrowLeft /> Πίσω
            </button>
            <h1>Ρυθμίσεις του {user}</h1>
            <p>Διαχείριση λογαριασμού και προτιμήσεων</p>
          </div>

          <div className="settings-options">
            {settingsOptions.map((option) => (
              <div
                key={option.id}
                className={`settings-option ${activeOption === option.id ? 'active' : ''}`}
                onClick={() => !option.noForm && setActiveOption(activeOption === option.id ? null : option.id)}
                style={{ '--option-color': option.color }}
              >
                <div className="option-header">
                  <div className="option-icon" style={{ color: option.color }}>
                    {option.icon}
                  </div>
                  <div className="option-info">
                    <h3>{option.title}</h3>
                    <p>{option.description}</p>
                  </div>
                </div>

                {option.id === 'avatar' && (
                  <div className="avatar-section">
                    <div className="avatar-preview">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Avatar" className="avatar-image" />
                      ) : (
                        <div className="avatar-placeholder">
                          <User />
                        </div>
                      )}
                    </div>
                    <div className="avatar-actions">
                      <label className="avatar-upload-btn">
                        <Camera /> Ανέβασμα Avatar
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {avatarPreview && (
                        <button onClick={handleRemoveAvatar} className="avatar-remove-btn">
                          <Trash2 /> Αφαίρεση
                        </button>
                      )}
                    </div>
                    <p className="avatar-note">Μέγιστο μέγεθος: 500KB</p>
                  </div>
                )}

                {activeOption === option.id && !option.noForm && (
                  <div className="option-form">
                    <form onSubmit={handleFormSubmit}>
                      {option.id === 'usernamechange' && (
                        <input
                          type="text"
                          placeholder="Εισαγωγή νέου username"
                          value={newusername}
                          onChange={(e) => setUsername(e.target.value)}
                          required
                        />
                      )}

                      {option.id === 'emailchange' && (
                        <input
                          type="email"
                          placeholder="Εισαγωγή νέου email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      )}

                      {option.id === 'passwordchange' && (
                        <>
                          <input
                            type="password"
                            placeholder="Εισαγωγή νέου password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                          />
                          <input
                            type="password"
                            placeholder="Επιβεβαίωση νέου password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                        </>
                      )}

                      {option.id === 'resetexams' && (
                        <div className="checkbox-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={resetexams}
                              onChange={(e) => setResetexams(e.target.checked)}
                            />
                            <span>Επιβεβαιώνω τη διαγραφή όλων των στατιστικών μου</span>
                          </label>
                        </div>
                      )}

                      {option.id === 'deleteaccount' && (
                        <div className="checkbox-group danger">
                          <label>
                            <input
                              type="checkbox"
                              checked={deleteac}
                              onChange={(e) => setDeleteac(e.target.checked)}
                            />
                            <span>Επιβεβαιώνω τη μόνιμη διαγραφή του λογαριασμού μου</span>
                          </label>
                        </div>
                      )}

                      <input
                        type="text"
                        value={hp}
                        onChange={(e) => setHP(e.target.value)}
                        style={{ display: 'none' }}
                      />

                      <div className="form-divider">
                        <p>Επιβεβαιώστε με τον τρέχων κωδικό σας την υποβολή των αλλαγών</p>
                      </div>

                      <input
                        type="password"
                        placeholder="Εισάγετε τον τρέχοντα κωδικό σας"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />

                      <div className="captcha-container">
                        <Captcha2 onVerify={setIsVerified} />
                      </div>

                      <button
                        type="submit"
                        className="submit-button"
                        disabled={!isVerified || isLoading}
                        style={{ backgroundColor: option.color }}
                      >
                        {isLoading ? 'Υποβολή...' : 'Υποβολή αλλαγών'}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
